// Started from shadcn/ui (new-york-v4) "avatar", restyled to Union. A member's or staff
// member's photo, or their initials (the brand's monogram) until they have one.

import { cn } from "@union/ui/lib/utils";
import { Avatar as AvatarPrimitive } from "radix-ui";
import type * as React from "react";

const sizes = { sm: "size-6 type-caption", default: "size-8 type-label", lg: "size-10 type-label" } as const;

function Avatar({
  className,
  size = "default",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> & { size?: keyof typeof sizes }) {
  return (
    <AvatarPrimitive.Root
      data-slot="avatar"
      className={cn("relative flex shrink-0 overflow-hidden rounded-pill", sizes[size], className)}
      {...props}
    />
  );
}

function AvatarImage({ className, ...props }: React.ComponentProps<typeof AvatarPrimitive.Image>) {
  return (
    <AvatarPrimitive.Image
      data-slot="avatar-image"
      className={cn("aspect-square size-full object-cover", className)}
      {...props}
    />
  );
}

/** Initials, e.g. "CB". Members' own avatar uses tone="primary". */
function AvatarFallback({
  className,
  tone = "neutral",
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> & { tone?: "neutral" | "primary" }) {
  return (
    <AvatarPrimitive.Fallback
      data-slot="avatar-fallback"
      className={cn(
        "flex size-full select-none items-center justify-center",
        tone === "primary" ? "bg-primary text-primary-foreground" : "bg-muted text-foreground",
        className,
      )}
      {...props}
    />
  );
}

export { Avatar, AvatarFallback, AvatarImage };
